import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { queryKeys } from './queryKeys';

// Bundle states where the agent is still settling after a deploy (CodeBuild is
// bundling sources). While ANY agent is in one of these, the list polls so a
// just-deployed agent's status advances live instead of looking stuck.
const TRANSITIONAL_BUNDLE = new Set(['pending', 'building', 'queued']);

/**
 * List deployed agents (workflows) for a project, React-Query cached.
 * Backed by GET /projects/:projectId/workflows — minimal per-workflow
 * metadata (workflowType, uuid, version, updatedAt, displayName, iconUrl,
 * bundleStatus, warmEnabled). Replaces PipelinesPage's manual fetch loop.
 */
export const useAgents = (projectId, options = {}) => {
  return useQuery({
    queryKey: queryKeys.agents.list(projectId),
    queryFn: async () => {
      const data = await api.get(`/projects/${projectId}/workflows`);
      return Array.isArray(data) ? data : [];
    },
    enabled: !!projectId,
    // Short stale window so landing on the Agents list (e.g. right after a
    // deploy) refetches and shows the new agent — instead of serving a
    // 5-min-old cache that omits it (the "new agent takes ~20min to appear"
    // bug; window-focus refetch is unreliable in the Electron studio app).
    staleTime: 10 * 1000,
    // Poll while any agent is still bundling OR has a run in flight, so a
    // just-deployed agent's status advances AND a "Running" pill flips back to
    // Sleeping/Live on its own when the run finishes — no manual refresh.
    refetchInterval: (query) => {
      const data = query?.state?.data;
      if (!Array.isArray(data)) return false;
      const settling = data.some((a) => TRANSITIONAL_BUNDLE.has(String(a?.bundleStatus || '').toLowerCase()));
      const running = data.some((a) => Number(a?.activeRuns) > 0);
      return settling || running ? 8000 : false;
    },
    ...options,
  });
};

/**
 * One agent's detail, React-Query cached. Backed by
 * GET /projects/:projectId/workflows/:workflowType — the live access pattern
 * the detail page uses (keyed by the immutable workflowType slug, NOT a uuid).
 *
 * Exported + ready for PipelineDetailPage to adopt later. It is NOT wired into
 * any page yet (PipelineDetailPage has another agent's uncommitted WIP and is
 * intentionally left untouched).
 */
export const useAgent = (projectId, workflowType, options = {}) => {
  return useQuery({
    queryKey: queryKeys.agents.detail(projectId, workflowType),
    queryFn: () => api.get(`/projects/${projectId}/workflows/${workflowType}`),
    enabled: !!projectId && !!workflowType,
    // Short stale window: the detail endpoint can return a transient
    // `notInstalled` 404-shell (template exists in the marketplace but isn't
    // deployed to this project yet). Caching that for 5min meant a deploy →
    // navigate-to-detail kept showing the stale "isn't deployed here yet" page.
    staleTime: 10 * 1000,
    // Poll every 5s while a run is in flight so the header pill flips back from
    // "Running" to Sleeping/Live on its own once the execution finishes.
    refetchInterval: (query) => (Number(query?.state?.data?.activeRuns) > 0 ? 5000 : false),
    ...options,
  });
};

/**
 * Delete an agent, then keep the cache honest:
 *   - invalidate the project's agent LIST → the deleted row leaves the table
 *   - remove the agent's DETAIL entry     → no ghost detail served from cache
 *
 * Backend wires DELETE on /workflows/:uuid (looks the workflow up via the UUID
 * GSI, verifies project membership server-side). Pass
 * `{ uuid, workflowType }` to `mutate()` — `uuid` for the request, both used
 * to scope the cache cleanup.
 */
export const useDeleteAgent = (projectId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid }) => api.delete(`/workflows/${uuid}`),
    onSuccess: (_data, { workflowType }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.list(projectId) });
      if (workflowType) {
        queryClient.removeQueries({ queryKey: queryKeys.agents.detail(projectId, workflowType) });
      }
      // Account-wide sidebar pinned list is its own query — refresh it so the
      // deleted agent drops out of the sidebar instantly (not just the
      // project's list table).
      queryClient.invalidateQueries({ queryKey: ['workspace', 'sidebar'] });
    },
  });
};
